import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBooking } from './BookingContext';
import { X, ChevronRight, Calendar, Clock, Check, ArrowLeft, Pencil } from 'lucide-react';
type Step = 'activate' | 'reasoning' | 'summary' | 'booking' | 'confirmed';
const predefinedIndustries = ['Hospitality', 'Conveyancing', 'Renewables', 'Maritime', 'Field Service'];
const industryWorkflows: Record<string, string[]> = {
  Hospitality: ['Reservation management', 'Guest messaging', 'Service recovery', 'Intelligent upsell'],
  Conveyancing: ['Document automation', 'Client updates', 'Settlement preparation', 'Compliance checking'],
  Renewables: ['Predictive maintenance', 'Fault triage', 'Technician dispatch', 'Performance reporting'],
  Maritime: ['Fleet coordination', 'Vessel reporting', 'Compliance management', 'Port orchestration'],
  'Field Service': ['Intelligent dispatch', 'Work order automation', 'Technician enablement', 'Parts inventory']
};
const industryFocus: Record<string, string> = {
  Hospitality: 'Guest experience automation and revenue optimisation',
  Conveyancing: 'Document workflow acceleration and client communication',
  Renewables: 'Asset uptime maximisation and dispatch efficiency',
  Maritime: 'Fleet intelligence and compliance automation',
  'Field Service': 'Dispatch optimisation and first-time fix rate improvement'
};
// Industry-specific cinematic background configurations
interface IndustryVisual {
  image: string;
  gradientFrom: string;
  gradientVia: string;
  accentGlow: string;
}
const industryVisuals: Record<string, IndustryVisual> = {
  Hospitality: {
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&q=80&auto=format&fit=crop',
    gradientFrom: 'from-amber-950/90',
    gradientVia: 'via-black/95',
    accentGlow: 'rgba(180, 120, 60, 0.08)'
  },
  Conveyancing: {
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80&auto=format&fit=crop',
    gradientFrom: 'from-slate-950/90',
    gradientVia: 'via-black/95',
    accentGlow: 'rgba(100, 130, 180, 0.08)'
  },
  Renewables: {
    image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920&q=80&auto=format&fit=crop',
    gradientFrom: 'from-emerald-950/90',
    gradientVia: 'via-black/95',
    accentGlow: 'rgba(60, 160, 100, 0.08)'
  },
  Maritime: {
    image: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5eb19?w=1920&q=80&auto=format&fit=crop',
    gradientFrom: 'from-blue-950/90',
    gradientVia: 'via-black/95',
    accentGlow: 'rgba(60, 100, 180, 0.08)'
  },
  'Field Service': {
    image: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1920&q=80&auto=format&fit=crop',
    gradientFrom: 'from-orange-950/90',
    gradientVia: 'via-black/95',
    accentGlow: 'rgba(200, 120, 50, 0.08)'
  }
};
const fallbackVisual: IndustryVisual = {
  image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80&auto=format&fit=crop',
  gradientFrom: 'from-gray-950/90',
  gradientVia: 'via-black/95',
  accentGlow: 'rgba(219, 127, 74, 0.08)'
};
function getVisual(industry: string | null): IndustryVisual {
  if (!industry) return fallbackVisual;
  return industryVisuals[industry] || fallbackVisual;
}
function getWorkflows(industry: string | null): string[] {
  if (!industry) return [];
  return industryWorkflows[industry] || ['Workflow mapping & analysis', 'Process automation', 'Integration design', 'Custom agent configuration'];
}
function getFocus(industry: string | null): string {
  if (!industry) return '';
  return industryFocus[industry] || `Tailored workflow automation and AI agent design for ${industry}`;
}
const reasoningSteps = ['Loading industry workflows', 'Applying operational context', 'Mapping bottlenecks', 'Preparing tailored demo'];
const timeSlots = [{
  day: 'Tomorrow',
  slots: ['10:00 AM', '2:00 PM', '4:00 PM']
}, {
  day: 'Wednesday',
  slots: ['9:00 AM', '11:00 AM', '3:00 PM']
}, {
  day: 'Thursday',
  slots: ['10:00 AM', '1:00 PM', '4:30 PM']
}];
const particles = Array.from({
  length: 20
}).map((_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  duration: 12 + Math.random() * 20,
  delay: Math.random() * 5
}));
export function BookingExperience() {
  const {
    isOpen,
    closeBooking
  } = useBooking();
  const [step, setStep] = useState<Step>('activate');
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const [reasoningIndex, setReasoningIndex] = useState(0);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // "Other" free text state
  const [isOtherMode, setIsOtherMode] = useState(false);
  const [otherValue, setOtherValue] = useState('');
  const otherInputRef = useRef<HTMLInputElement>(null);
  // Background image preload state
  const [bgLoaded, setBgLoaded] = useState(false);
  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setStep('activate');
      setSelectedIndustry(null);
      setReasoningIndex(0);
      setSelectedDay(null);
      setSelectedSlot(null);
      setName('');
      setEmail('');
      setIsSubmitting(false);
      setIsOtherMode(false);
      setOtherValue('');
      setBgLoaded(false);
    }
  }, [isOpen]);
  // Preload industry background when industry is selected
  useEffect(() => {
    if (selectedIndustry && step === 'reasoning') {
      const visual = getVisual(selectedIndustry);
      const img = new Image();
      img.onload = () => setBgLoaded(true);
      img.src = visual.image;
    }
  }, [selectedIndustry, step]);
  // Focus "Other" input when entering other mode
  useEffect(() => {
    if (isOtherMode) {
      setTimeout(() => otherInputRef.current?.focus(), 100);
    }
  }, [isOtherMode]);
  const handleIndustrySelect = useCallback((industry: string) => {
    setSelectedIndustry(industry);
    setStep('reasoning');
    setReasoningIndex(0);
    reasoningSteps.forEach((_, index) => {
      setTimeout(() => {
        setReasoningIndex(index + 1);
        if (index === reasoningSteps.length - 1) {
          setTimeout(() => setStep('summary'), 1200);
        }
      }, (index + 1) * 1000);
    });
  }, []);
  const handleOtherSubmit = useCallback(() => {
    if (!otherValue.trim()) return;
    handleIndustrySelect(otherValue.trim());
  }, [otherValue, handleIndustrySelect]);
  const handleSubmit = useCallback(() => {
    if (!name.trim() || !email.trim() || !selectedDay || !selectedSlot) return;
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setStep('confirmed');
    }, 1500);
  }, [name, email, selectedDay, selectedSlot]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeBooking();
    };
    if (isOpen) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeBooking]);
  const showCinematicBg = step === 'summary' || step === 'booking' || step === 'confirmed';
  const visual = getVisual(selectedIndustry);
  return <AnimatePresence data-id="element-137">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.5
    }} className="fixed inset-0 z-[100] bg-black flex items-center justify-center overflow-y-auto" data-id="element-138">
          {/* === CINEMATIC INDUSTRY BACKGROUND === */}
          <AnimatePresence data-id="element-139">
            {showCinematicBg && bgLoaded && <motion.div key={`bg-${selectedIndustry}`} initial={{
          opacity: 0,
          scale: 1.1
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 1.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="absolute inset-0 z-0 overflow-hidden" data-id="element-140">
                {/* Image layer with slow ambient drift */}
                <motion.div className="absolute inset-0" animate={{
            scale: [1, 1.05, 1],
            x: [0, 10, 0],
            y: [0, -5, 0]
          }} transition={{
            duration: 30,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-141">
                  <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: `url(${visual.image})`
            }} data-id="element-142" />
                </motion.div>

                {/* Dark gradient overlays for depth and readability */}
                <div className={`absolute inset-0 bg-gradient-to-b ${visual.gradientFrom} ${visual.gradientVia} to-black`} data-id="element-143" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/80" data-id="element-144" />
                <div className="absolute inset-0 bg-black/40" data-id="element-145" />

                {/* Cinematic vignette */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,black_100%)]" data-id="element-146" />

                {/* Accent glow — industry-tinted */}
                <motion.div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full blur-[150px] pointer-events-none" style={{
            backgroundColor: visual.accentGlow
          }} animate={{
            opacity: [0.5, 1, 0.5]
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-147" />

                {/* Subtle light sweep */}
                <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.02] to-transparent" animate={{
            x: ['-100%', '100%']
          }} transition={{
            duration: 12,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-148" />
              </motion.div>}
          </AnimatePresence>

          {/* === DEFAULT BACKGROUND (particles + grid) for early steps === */}
          {!showCinematicBg && <>
              <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10" data-id="element-149">
                {particles.map(p => <motion.div key={p.id} className="absolute w-px h-px bg-white rounded-full" style={{
            left: `${p.x}%`,
            top: `${p.y}%`
          }} animate={{
            y: [0, -80, 0],
            x: [0, Math.random() * 30 - 15, 0],
            opacity: [0, 0.6, 0]
          }} transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: 'linear'
          }} data-id="element-150" />)}
              </div>
              <div className="absolute inset-0 opacity-5 pointer-events-none" data-id="element-151">
                <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-152">
                  <defs data-id="element-153">
                    <pattern id="booking-grid" width="60" height="60" patternUnits="userSpaceOnUse" data-id="element-154">
                      <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.5" data-id="element-155" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#booking-grid)" data-id="element-156" />
                </svg>
              </div>
            </>}

          {/* Close button */}
          <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3
      }} onClick={closeBooking} className="absolute top-6 right-6 z-50 w-10 h-10 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:border-white/30 transition-all" data-id="element-157">
            <X className="w-5 h-5" data-id="element-158" />
          </motion.button>

          {/* Content */}
          <div className="relative z-10 w-full max-w-2xl mx-auto px-6 py-20" data-id="element-159">
            <AnimatePresence mode="wait" data-id="element-160">
              {/* STEP 1: Activate + Industry Selection */}
              {step === 'activate' && <motion.div key="activate" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -30
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="flex flex-col items-center text-center" data-id="element-161">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="w-16 h-16 rounded-full bg-brand-orange/10 border border-brand-orange/30 flex items-center justify-center mb-8 relative" data-id="element-162">
                    <div className="w-3 h-3 rounded-full bg-brand-orange" data-id="element-163" />
                    <motion.div className="absolute inset-0 rounded-full border border-brand-orange/50" animate={{
                scale: [1, 1.5, 1],
                opacity: [1, 0, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-164" />
                  </motion.div>

                  <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.4
            }} className="text-sm font-mono text-brand-orange uppercase tracking-widest mb-4" data-id="element-165">
                    Aijentik Agent Activated.
                  </motion.p>

                  <motion.h2 initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.7
            }} className="font-display text-3xl md:text-4xl text-white mb-12 tracking-tight" data-id="element-166">
                    What industry are you in?
                  </motion.h2>

                  <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 1
            }} className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full max-w-lg" data-id="element-167">
                    {predefinedIndustries.map((industry, i) => <motion.button key={industry} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 1 + i * 0.1
              }} whileHover={{
                scale: 1.03
              }} whileTap={{
                scale: 0.97
              }} onClick={() => handleIndustrySelect(industry)} className="px-6 py-4 bg-[#0A0A0A] border border-white/10 rounded-2xl text-sm font-medium text-gray-300 hover:text-white hover:border-brand-orange/50 hover:bg-brand-orange/5 transition-all duration-300" data-id="element-168">
                        {industry}
                      </motion.button>)}

                    {/* "Other" option — transforms into input */}
                    <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 1 + predefinedIndustries.length * 0.1
              }} className="relative" data-id="element-169">
                      <AnimatePresence mode="wait" data-id="element-170">
                        {!isOtherMode ? <motion.button key="other-btn" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.95
                  }} whileHover={{
                    scale: 1.03
                  }} whileTap={{
                    scale: 0.97
                  }} onClick={() => setIsOtherMode(true)} className="w-full px-6 py-4 bg-[#0A0A0A] border border-white/10 rounded-2xl text-sm font-medium text-gray-400 hover:text-white hover:border-white/20 transition-all duration-300 flex items-center justify-center gap-2" data-id="element-171">
                            <Pencil className="w-3 h-3" data-id="element-172" />
                            Other
                          </motion.button> : <motion.div key="other-input" initial={{
                    opacity: 0,
                    scale: 0.95
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} className="col-span-2 md:col-span-3" data-id="element-173">
                            <div className="flex items-center gap-2 bg-[#0A0A0A] border border-brand-orange/30 rounded-2xl px-4 py-1 focus-within:border-brand-orange/60 transition-colors" data-id="element-174">
                              <input ref={otherInputRef} type="text" value={otherValue} onChange={e => setOtherValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleOtherSubmit()} placeholder="Type your industry" className="flex-1 bg-transparent text-white text-sm placeholder:text-gray-600 focus:outline-none py-3" data-id="element-175" />
                              <motion.button whileHover={{
                        scale: 1.05
                      }} whileTap={{
                        scale: 0.95
                      }} onClick={handleOtherSubmit} disabled={!otherValue.trim()} className="p-2 rounded-xl bg-brand-orange/10 text-brand-orange hover:bg-brand-orange/20 disabled:opacity-30 transition-all" data-id="element-176">
                                <ChevronRight className="w-4 h-4" data-id="element-177" />
                              </motion.button>
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </motion.div>
                  </motion.div>

                  {/* Expanded input below grid when in other mode */}
                  <AnimatePresence data-id="element-178">
                    {isOtherMode && <motion.button initial={{
                opacity: 0,
                y: 5
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0
              }} onClick={() => {
                setIsOtherMode(false);
                setOtherValue('');
              }} className="mt-4 text-xs text-gray-600 hover:text-gray-400 transition-colors" data-id="element-179">
                        ← Back to options
                      </motion.button>}
                  </AnimatePresence>
                </motion.div>}

              {/* STEP 2: Reasoning */}
              {step === 'reasoning' && <motion.div key="reasoning" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -30
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="flex flex-col items-center text-center" data-id="element-180">
                  <motion.div className="w-16 h-16 rounded-full bg-brand-orange/10 border border-brand-orange/30 flex items-center justify-center mb-10 relative" data-id="element-181">
                    <motion.div className="w-3 h-3 rounded-full bg-brand-orange" animate={{
                scale: [1, 1.3, 1]
              }} transition={{
                duration: 1,
                repeat: Infinity
              }} data-id="element-182" />
                  </motion.div>

                  <p className="text-sm font-mono text-gray-500 uppercase tracking-widest mb-2" data-id="element-183">
                    Analysing
                  </p>
                  <h3 className="font-display text-2xl text-white mb-12 tracking-tight" data-id="element-184">
                    {selectedIndustry}
                  </h3>

                  <div className="space-y-6 w-full max-w-sm" data-id="element-185">
                    {reasoningSteps.map((stepText, index) => {
                const isActive = reasoningIndex === index + 1;
                const isCompleted = reasoningIndex > index + 1;
                return <motion.div key={index} initial={{
                  opacity: 0.2
                }} animate={{
                  opacity: isActive || isCompleted ? 1 : 0.2
                }} className="flex items-center gap-4" data-id="element-186">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${isActive ? 'bg-brand-orange/20 border border-brand-orange/50' : isCompleted ? 'bg-white/10 border border-white/20' : 'bg-[#111] border border-white/5'}`} data-id="element-187">
                            {isCompleted ? <Check className="w-3 h-3 text-gray-400" data-id="element-188" /> : isActive ? <motion.div className="w-2 h-2 rounded-full bg-brand-orange" animate={{
                      scale: [1, 1.3, 1]
                    }} transition={{
                      duration: 0.8,
                      repeat: Infinity
                    }} data-id="element-189" /> : <div className="w-1.5 h-1.5 rounded-full bg-gray-700" data-id="element-190" />}
                          </div>
                          <span className={`text-sm font-medium transition-colors duration-500 ${isActive ? 'text-white' : isCompleted ? 'text-gray-400' : 'text-gray-700'}`} data-id="element-191">
                            {stepText}
                          </span>
                          {isActive && <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: '100%'
                  }} transition={{
                    duration: 0.8,
                    ease: 'linear'
                  }} className="flex-1 h-px bg-brand-orange/50" data-id="element-192" />}
                        </motion.div>;
              })}
                  </div>
                </motion.div>}

              {/* STEP 3: Summary — with cinematic background */}
              {step === 'summary' && selectedIndustry && <motion.div key="summary" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -30
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="flex flex-col items-center text-center" data-id="element-193">
                  <motion.div initial={{
              scale: 0.8,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.6
            }} className="w-12 h-12 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-8 backdrop-blur-sm" data-id="element-194">
                    <Check className="w-5 h-5 text-green-400" data-id="element-195" />
                  </motion.div>

                  <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.2
            }} className="text-sm font-mono text-brand-orange uppercase tracking-widest mb-3" data-id="element-196">
                    Analysis Complete
                  </motion.p>
                  <motion.h3 initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="font-display text-2xl md:text-3xl text-white mb-10 tracking-tight" data-id="element-197">
                    We'll tailor this demo to your business.
                  </motion.h3>

                  <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5,
              duration: 0.6
            }} className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8 w-full max-w-md text-left mb-10 shadow-2xl" data-id="element-198">
                    <div className="space-y-6" data-id="element-199">
                      <div data-id="element-200">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-2" data-id="element-201">
                          Industry
                        </p>
                        <p className="text-white font-medium" data-id="element-202">
                          {selectedIndustry}
                        </p>
                      </div>
                      <div data-id="element-203">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-2" data-id="element-204">
                          Key Workflows
                        </p>
                        <div className="flex flex-wrap gap-2" data-id="element-205">
                          {getWorkflows(selectedIndustry).map((w, i) => <motion.span key={i} initial={{
                      opacity: 0,
                      scale: 0.9
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} transition={{
                      delay: 0.6 + i * 0.08
                    }} className="text-xs text-gray-300 bg-white/5 backdrop-blur-sm border border-white/10 px-3 py-1 rounded-full" data-id="element-206">
                              {w}
                            </motion.span>)}
                        </div>
                      </div>
                      <div data-id="element-207">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-2" data-id="element-208">
                          Estimated Demo Focus
                        </p>
                        <p className="text-gray-300 text-sm" data-id="element-209">
                          {getFocus(selectedIndustry)}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.button initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.8
            }} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => setStep('booking')} className="px-8 py-4 bg-brand-orange hover:bg-brand-deep-orange text-white font-medium rounded-full transition-colors duration-300 flex items-center gap-2 shadow-[0_0_20px_rgba(219,127,74,0.3)] hover:shadow-[0_0_40px_rgba(219,127,74,0.5)]" data-id="element-210">
                    Schedule Your Demo
                    <ChevronRight className="w-4 h-4" data-id="element-211" />
                  </motion.button>
                </motion.div>}

              {/* STEP 4: Booking Interface — cinematic bg persists */}
              {step === 'booking' && <motion.div key="booking" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -30
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="flex flex-col items-center" data-id="element-212">
                  <button onClick={() => setStep('summary')} className="self-start flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-8" data-id="element-213">
                    <ArrowLeft className="w-4 h-4" data-id="element-214" />
                    Back
                  </button>

                  <Calendar className="w-8 h-8 text-brand-orange mb-6" data-id="element-215" />
                  <h3 className="font-display text-2xl md:text-3xl text-white mb-10 tracking-tight text-center" data-id="element-216">
                    Choose a Time
                  </h3>

                  <div className="w-full max-w-md space-y-6 mb-10" data-id="element-217">
                    {timeSlots.map(dayGroup => <div key={dayGroup.day} data-id="element-218">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-3" data-id="element-219">
                          {dayGroup.day}
                        </p>
                        <div className="flex gap-3" data-id="element-220">
                          {dayGroup.slots.map(slot => {
                    const isSelected = selectedDay === dayGroup.day && selectedSlot === slot;
                    return <button key={slot} onClick={() => {
                      setSelectedDay(dayGroup.day);
                      setSelectedSlot(slot);
                    }} className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all duration-300 border backdrop-blur-sm ${isSelected ? 'bg-brand-orange/10 border-brand-orange/50 text-white shadow-[0_0_15px_rgba(219,127,74,0.15)]' : 'bg-black/40 border-white/10 text-gray-400 hover:border-white/20 hover:text-white'}`} data-id="element-221">
                                <Clock className="w-3 h-3 inline mr-1.5 -mt-0.5" data-id="element-222" />
                                {slot}
                              </button>;
                  })}
                        </div>
                      </div>)}
                  </div>

                  <div className="w-full max-w-md space-y-4 mb-10" data-id="element-223">
                    <input type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} className="w-full bg-black/40 backdrop-blur-sm border border-white/10 rounded-xl px-5 py-4 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-brand-orange/50 transition-colors" data-id="element-224" />
                    <input type="email" placeholder="Work email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-black/40 backdrop-blur-sm border border-white/10 rounded-xl px-5 py-4 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-brand-orange/50 transition-colors" data-id="element-225" />
                  </div>

                  <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={handleSubmit} disabled={!name.trim() || !email.trim() || !selectedDay || !selectedSlot || isSubmitting} className="px-8 py-4 bg-brand-orange hover:bg-brand-deep-orange disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium rounded-full transition-all duration-300 flex items-center gap-2 shadow-[0_0_20px_rgba(219,127,74,0.3)] hover:shadow-[0_0_40px_rgba(219,127,74,0.5)]" data-id="element-226">
                    {isSubmitting ? <motion.div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full" animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-227" /> : <>
                        Confirm Demo
                        <ChevronRight className="w-4 h-4" data-id="element-228" />
                      </>}
                  </motion.button>
                </motion.div>}

              {/* STEP 5: Confirmation — cinematic bg persists */}
              {step === 'confirmed' && <motion.div key="confirmed" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="flex flex-col items-center text-center" data-id="element-229">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              duration: 0.5,
              type: 'spring',
              bounce: 0.3
            }} className="w-20 h-20 rounded-full bg-green-500/10 backdrop-blur-sm border border-green-500/30 flex items-center justify-center mb-10 relative" data-id="element-230">
                    <Check className="w-8 h-8 text-green-400" data-id="element-231" />
                    <motion.div className="absolute inset-0 rounded-full bg-green-500/10" animate={{
                scale: [1, 1.5],
                opacity: [0.5, 0]
              }} transition={{
                duration: 1.5,
                repeat: Infinity
              }} data-id="element-232" />
                  </motion.div>

                  <motion.h3 initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="font-display text-3xl md:text-4xl text-white mb-4 tracking-tight" data-id="element-233">
                    Your demo is confirmed.
                  </motion.h3>

                  <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.6
            }} className="text-gray-400 text-lg mb-4" data-id="element-234">
                    We're preparing your workflows now.
                  </motion.p>

                  <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.9
            }} className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 w-full max-w-sm mb-12 shadow-2xl" data-id="element-235">
                    <div className="space-y-3 text-sm" data-id="element-236">
                      <div className="flex justify-between" data-id="element-237">
                        <span className="text-gray-500" data-id="element-238">Industry</span>
                        <span className="text-white font-medium" data-id="element-239">
                          {selectedIndustry}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-240">
                        <span className="text-gray-500" data-id="element-241">Date</span>
                        <span className="text-white font-medium" data-id="element-242">
                          {selectedDay}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-243">
                        <span className="text-gray-500" data-id="element-244">Time</span>
                        <span className="text-white font-medium" data-id="element-245">
                          {selectedSlot}
                        </span>
                      </div>
                    </div>
                  </motion.div>

                  <motion.button initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 1.2
            }} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={closeBooking} className="px-8 py-4 bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/30 text-white font-medium rounded-full transition-all duration-300" data-id="element-246">
                    Return to Site
                  </motion.button>
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>}
    </AnimatePresence>;
}